import EditablePayrollRow from "./EditablePayrollRow";
import "./PayrollRulesPage.css";
// src/popup/AddAbsenceTypesPopup.js
import React, { useState, useEffect, useRef } from "react";
import {
  FaSave,
  FaSearch,
  FaArrowLeft,
  FaUserSlash,
} from "react-icons/fa";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { useCompany } from "../contexts/CompanyContext"; // Lägg till denna import

// Popup-komponent för att hantera frånvarotyper
export default function AddAbsenceTypesPopup({ isOpen, onClose, onSave }) {
  const { activeCompany } = useCompany(); // Använd activeCompany

  const formRef = useRef(null);

  // State för filtrering och UI
  const [searchTerm, setSearchTerm] = useState("");

  // State för formulär
  const [typeName, setTypeName] = useState("");
  const [salaryPercent, setSalaryPercent] = useState("");
  const [description, setDescription] = useState("");

  // State för data och laddning
  const [editingId, setEditingId] = useState(null);
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Hämta företagskontots UID från activeCompany
  const accountToUse = activeCompany?.companyAccountUid;

  // Hjälpfunktion för att hämta collection för frånvarotyper
  const getAbsenceTypesCollection = () => {
    if (!accountToUse) return null;
    return collection(db, "users", accountToUse, "absenceTypes");
  };

  // Hjälpfunktion för att hämta dokument för en specifik frånvarotyp
  const getAbsenceTypeDoc = (typeId) => {
    if (!accountToUse || !typeId) return null;
    return doc(db, "users", accountToUse, "absenceTypes", typeId);
  };

  // Hämta frånvarotyper från Firestore i realtid
  useEffect(() => {
    if (!accountToUse || !isOpen) return;

    console.log("Hämtar frånvarotyper för företag:", activeCompany?.companyName);
    console.log("Company Account UID:", accountToUse);

    const absenceTypesRef = getAbsenceTypesCollection();
    if (!absenceTypesRef) return;

    const q = query(absenceTypesRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));
        setAbsenceTypes(items);
      },
      (error) => {
        console.error("Fel vid hämtning av frånvarotyper:", error);
      }
    );

    return () => unsubscribe();
  }, [accountToUse, isOpen, activeCompany?.companyName]);

  // Stäng popup om den inte är öppen
  if (!isOpen) return null;

  // Återställ formulär
  const resetForm = () => {
    setTypeName("");
    setSalaryPercent("");
    setDescription("");
    setEditingId(null);
    setSaveSuccess(false);
  };

  // Hantera formulärinskickning (skapa eller uppdatera)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accountToUse) return;

    setLoading(true);
    setSaveSuccess(false);

    try {
      const trimmedName = typeName.trim();
      const trimmedDescription = description.trim();
      const percentValue = Number(salaryPercent);

      // Validering
      if (!trimmedName || salaryPercent === "") {
        setLoading(false);
        return;
      }

      // Uppdatera befintlig frånvarotyp
      if (editingId) {
        const absenceTypeRef = getAbsenceTypeDoc(editingId);
        if (!absenceTypeRef) return;

        const updatedType = {
          name: trimmedName,
          salaryPercent: percentValue,
          description: trimmedDescription,
          updatedAt: new Date().toISOString(),
        };

        await updateDoc(absenceTypeRef, updatedType);

        if (onSave) {
          onSave({
            id: editingId,
            ...updatedType,
            action: "update",
          });
        }

        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } 
      // Skapa ny frånvarotyp
      else {
        const absenceTypesRef = getAbsenceTypesCollection();
        if (!absenceTypesRef) return;

        const newType = {
          name: trimmedName,
          salaryPercent: percentValue,
          description: trimmedDescription,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: accountToUse,
          companyId: activeCompany?.companyId || "",
          companyName: activeCompany?.companyName || "",
        };

        const docRef = await addDoc(absenceTypesRef, newType);

        if (onSave) {
          onSave({
            id: docRef.id,
            ...newType,
            action: "create",
          });
        }

        resetForm();

        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (error) {
      console.error("Fel vid sparande av frånvarotyp:", error);
    } finally {
      setLoading(false);
    }
  };

  // Hantera redigering av en frånvarotyp
  // Hantera borttagning av en frånvarotyp
  const handleDelete = async (id) => {
    if (!accountToUse || !id) return;

    if (window.confirm("Är du säker på att du vill ta bort denna frånvarotyp?")) {
      try {
        const deletedType = absenceTypes.find((item) => item.id === id);
        const absenceTypeRef = getAbsenceTypeDoc(id);
        if (!absenceTypeRef) return;

        await deleteDoc(absenceTypeRef);

        if (onSave) {
          onSave({ ...deletedType, action: "delete" });
        }
      } catch (error) {
        console.error("Fel vid borttagning av frånvarotyp:", error);
      }
    }
  };

  // Stäng popup och återställ alla states
  const handleClose = () => {
    resetForm();
    setSearchTerm("");


    onClose();
  };



  // Filtrera frånvarotyper baserat på sökterm
  const filteredAbsenceTypes = absenceTypes.filter((item) => {
    const search = searchTerm.toLowerCase();

    return (
      item.name?.toLowerCase().includes(search) ||
      item.description?.toLowerCase().includes(search) ||
      String(item.salaryPercent ?? "").includes(search)
    );
  });

  // Hjälpfunktion för att visa text för löneandel
  const getSalaryText = (percent) => {
    const value = Number(percent || 0);
    return `${value}% av timlönen`;
  };

  // Stilar för komponenten
  const styles = {
    header: {
      padding: "24px 24px 16px 24px",
      borderBottom: "1px solid #eef2f7",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#ffffff",
      position: "sticky",
      top: 0,
      zIndex: 10,
    },
    title: {
      margin: 0,
      fontSize: "1.5rem",
      fontWeight: 700,
      color: "#1f2937",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    closeButton: {
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "8px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#6b7280",
      transition: "all 0.2s",
    },
    backButton: {
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "8px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#007bff",
      transition: "all 0.2s",
      marginRight: "8px",
    },
    content: {
      padding: "24px",
    },
    introText: {
      fontSize: "14px",
      lineHeight: 1.6,
      color: "#6b7280",
      marginBottom: "20px",
    },
    searchSection: {
      marginBottom: "20px",
    },
    searchContainer: {
      position: "relative",
      marginBottom: "12px",
    },
    searchIcon: {
      position: "absolute",
      left: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#9ca3af",
    },
    searchInput: {
      width: "100%",
      padding: "12px 16px 12px 40px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
      fontSize: "14px",
      outline: "none",
      transition: "border-color 0.2s",
      boxSizing: "border-box",
    },
    createButtonRow: {
      display: "flex",
      justifyContent: "flex-end",
    },
    createButton: {
      padding: "14px 22px",
      borderRadius: "16px",
      border: "none",
      backgroundColor: "#3b93e3",
      color: "#ffffff",
      fontSize: "15px",
      fontWeight: 700,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      whiteSpace: "nowrap",
      transition: "background-color 0.2s",
    },
    rulesList: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      marginTop: "20px",
    },
    ruleItem: {
      padding: "20px 24px",
      borderRadius: "24px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#f9fafb",
      border: "1px solid #e5e7eb",
    },
    ruleInfo: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      minWidth: 0,
    },
    ruleName: {
      fontSize: "18px",
      fontWeight: 700,
      color: "#25324a",
    },
    ruleMeta: {
      fontSize: "14px",
      color: "#6b7280",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      flexWrap: "wrap",
    },
    percentBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      backgroundColor: "transparent",
      color: "#6b7280",
      borderRadius: "999px",
      padding: "0px",
      fontSize: "13px",
      fontWeight: 600,
      width: "fit-content",
    },
    descriptionText: {
      fontSize: "13px",
      color: "#6b7280",
      lineHeight: 1.4,
    },
    actionButtons: {
      display: "flex",
      gap: "8px",
      marginLeft: "12px",
      flexShrink: 0,
    },
    editButton: {
      width: "56px",
      height: "56px",
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      cursor: "pointer",
      borderRadius: "18px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#4b5563",
      transition: "all 0.2s",
    },
    deleteButton: {
      width: "56px",
      height: "56px",
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      cursor: "pointer",
      borderRadius: "18px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#4b5563",
      transition: "all 0.2s",
    },
    panelHeader: {
      padding: "24px 24px 16px 24px",
      borderBottom: "1px solid #eef2f7",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      backgroundColor: "#ffffff",
      position: "sticky",
      top: 0,
      zIndex: 10,
    },
    panelTitle: {
      margin: 0,
      fontSize: "1.5rem",
      fontWeight: 700,
      color: "#1f2937",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    panelForm: {
      padding: "24px",
    },
    formGroup: {
      marginBottom: "24px",
    },
    label: {
      display: "block",
      fontSize: "14px",
      fontWeight: 600,
      color: "#374151",
      marginBottom: "8px",
    },
    helperText: {
      fontSize: "12px",
      color: "#6b7280",
      marginBottom: "10px",
      lineHeight: 1.5,
    },
    input: {
      width: "100%",
      padding: "12px 16px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
      fontSize: "14px",
      outline: "none",
      transition: "border-color 0.2s",
      boxSizing: "border-box",
    },
    textarea: {
      width: "100%",
      padding: "12px 16px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
      fontSize: "14px",
      outline: "none",
      minHeight: "90px",
      resize: "vertical",
      fontFamily: "inherit",
      boxSizing: "border-box",
    },
    inputContainer: {
      position: "relative",
    },
    percentSymbol: {
      position: "absolute",
      right: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#6b7280",
      fontSize: "14px",
      fontWeight: 500,
    },
    previewBox: {
      marginTop: "16px",
      backgroundColor: "#f8fafc",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      padding: "14px",
    },
    previewTitle: {
      fontSize: "13px",
      fontWeight: 700,
      color: "#374151",
      marginBottom: "8px",
    },
    previewText: {
      fontSize: "13px",
      color: "#4b5563",
      lineHeight: 1.5,
    },
    buttonGroup: {
      display: "flex",
      gap: "12px",
      marginTop: "32px",
    },
    saveButton: {
      flex: 1,
      padding: "14px 20px",
      borderRadius: "12px",
      border: "none",
      backgroundColor: "#007bff",
      color: "#ffffff",
      fontSize: "15px",
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      transition: "background-color 0.2s",
      opacity: loading ? 0.7 : 1,
    },
    cancelButton: {
      flex: 1,
      padding: "14px 20px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
      backgroundColor: "#ffffff",
      color: "#4b5563",
      fontSize: "15px",
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      transition: "background-color 0.2s",
    },
    divider: {
      height: "1px",
      backgroundColor: "#eef2f7",
      margin: "24px 0",
    },
    infoText: {
      fontSize: "13px",
      color: "#6b7280",
      marginTop: "16px",
      fontStyle: "italic",
      textAlign: "center",
    },
    emptyState: {
      textAlign: "center",
      padding: "48px 20px",
      color: "#6b7280",
      fontSize: "14px",
      border: "1px dashed #d1d5db",
      borderRadius: "16px",
      marginTop: "20px",
    },
    successMessage: {
      backgroundColor: "#10b981",
      color: "#ffffff",
      padding: "10px",
      borderRadius: "8px",
      textAlign: "center",
      marginTop: "16px",
      fontSize: "14px",
    },
  };

  Object.assign(styles, {
    header: { padding: "20px 12px 16px", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 20 },
    title: { margin: 0, display: "flex", alignItems: "center", gap: 12, fontSize: 24, fontWeight: 800, color: "#111827" },
    content: { padding: 0 },
    buttonGroup: { display: "flex", justifyContent: "flex-end", marginTop: 20 },
    saveButton: { ...styles.saveButton, flex: "initial", borderRadius: 8, padding: "10px 20px", backgroundColor: "#1677ff" },
  });
  return (
    <section className="payroll-rules-page" style={{ width: "100%", minWidth: 0, background: "#f7f7f8" }} aria-label="Frånvarotyper">
      <header style={styles.header}>
        <button type="button" onClick={handleClose} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", border: "1px solid #3b82f6", borderRadius: 8, color: "#2563eb", background: "white", cursor: "pointer", fontWeight: 700 }}><FaArrowLeft />Tillbaka till löner</button>
        <h1 style={styles.title}><FaUserSlash color="#1677ff" />Frånvarotyper</h1>
      </header>
      <div style={{ padding: "0 12px 16px" }}>
        <section style={{ padding: 20, border: "1px solid #d1d5db", borderRadius: 8, background: "white", marginBottom: 24 }}>
          <h2 style={{ margin: "0 0 20px", fontSize: 16 }}>{editingId ? "Redigera" : "Skapa ny frånvarotyp"}</h2>
              <form ref={formRef} className="payroll-rule-form" onSubmit={handleSubmit}>
                {/* Namn-fält */}
                <div style={styles.formGroup}>
                  <label className="payroll-label" style={styles.label}>Namn</label>
                  <input
                    type="text"
                    className="payroll-field" style={styles.input}
                    value={typeName}
                    onChange={(e) => setTypeName(e.target.value)}
                    placeholder="t.ex. Sjukfrånvaro"
                    required
                  />
                </div>

                {/* Löneandel-fält */}
                <div style={styles.formGroup}>
                  <label className="payroll-label" style={styles.label}>
                    Frånvarolön (i % av timlönen)
                  </label>
                  <div style={styles.helperText}>
                    T.ex. 100 = full lön. 0 = ingen lön. 80 = 80% av timlönen.
                  </div>

                  <div style={styles.inputContainer}>
                    <input
                      type="number"
                      className="payroll-field" style={styles.input}
                      value={salaryPercent}
                      onChange={(e) => setSalaryPercent(e.target.value)}
                      placeholder="t.ex. 80"
                      required
                      min="0"
                      max="100"
                      step="1"
                    />
                    <span style={styles.percentSymbol}>%</span>
                  </div>

                  {/* Förhandsvisning */}
                  {salaryPercent !== "" && (
                    <div style={styles.previewBox}>
                      <div style={styles.previewTitle}>Förhandsvisning</div>
                      <div style={styles.previewText}>
                        Denna frånvarotyp ger <strong>{Number(salaryPercent)}%</strong> av timlönen.
                      </div>
                    </div>
                  )}
                </div>

                {/* Beskrivning-fält */}
                <div style={styles.formGroup}>
                  <label className="payroll-label" style={styles.label}>Beskrivning (valfri)</label>
                  <textarea
                    className="payroll-field payroll-textarea" style={styles.textarea}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="t.ex. Gäller vid sjukdom"
                  />
                </div>



                {/* Knappar */}
                <div style={styles.buttonGroup}>

                  <button
                    type="submit"
                    className="payroll-primary-button" style={styles.saveButton}
                    disabled={loading}
                  >
                    <FaSave />
                    {loading ? "Sparar..." : "Skapa"}
                  </button>
                </div>

                {/* Succémeddelande */}
                {saveSuccess && (
                  <div className="payroll-success" style={styles.successMessage}>
                    Frånvarotypen sparades framgångsrikt!
                  </div>
                )}


              </form>
        </section>
        <section><h2 style={{ fontSize: 16, margin: "0 0 20px" }}>Alla frånvarotyper</h2>
            <div style={styles.content}>

              {/* Sökfält och skapa-knapp */}
              <div style={styles.searchSection}>
                <div style={styles.searchContainer}>
                  <FaSearch style={styles.searchIcon} size={16} />
                  <input
                    type="text"
                    className="payroll-field payroll-search" style={styles.searchInput}
                    placeholder="Sök frånvarotyp..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>


              </div>

              <div className="payroll-table-wrap"><table className="payroll-table">
                <thead><tr><th>Namn</th><th>Löneandel</th><th>Beskrivning</th><th>Åtgärder</th></tr></thead>
                <tbody>{filteredAbsenceTypes.map(rule => <EditablePayrollRow key={rule.id} item={rule} companyId={accountToUse} collectionName="absenceTypes" onDelete={item => handleDelete(item.id)} onSaved={onSave} columns={[
                  { value: rule.name, fields: [{ key: "name", label: "Namn i raden", required: true }] },
                  { value: getSalaryText(rule.salaryPercent), fields: [{ key: "salaryPercent", label: "Procent i raden", type: "number", min: 0, max: 100, required: true }] },
                  { value: rule.description || "–", fields: [{ key: "description", label: "Beskrivning i raden", type: "textarea" }] },
                ]} />)}
                {!filteredAbsenceTypes.length && <tr><td colSpan={4} className="payroll-empty">Inga regler att visa.</td></tr>}</tbody>
              </table></div>
            </div>
        </section>
      </div>
    </section>
  );
}
